/**
 * Disk recovery — UI layer.
 *
 * The image is never loaded into memory: a card dump is routinely 64 GB, and
 * every read goes through a byte-range reader over the File. That is also why
 * the engines take `read(offset, length)` rather than a buffer.
 */

import { findVolumes } from './volumes.js';
import { listTree, planRead, readEntry } from './fat-reader.js';
import { carveAll } from './carver.js';
import { verify } from './carver.js';

const T = (k, p) => {
  let s = (window.__DR_I18N && window.__DR_I18N[k]) || k;
  if (p) for (const a of Object.keys(p).sort((x, y) => y.length - x.length)) {
    s = s.split(':' + a).join(p[a]);
  }
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

const state = { file: null, read: null, volumes: [], entries: [], abort: null };

function show(html) { const o = el('dr-out'); o.innerHTML = html; o.hidden = false; }
const card = (tone, title, body, extra = '') =>
  `<div class="dr-card dr-tone-${tone}"><p class="dr-title">${title}</p>
     <p class="dr-body">${body}</p>${extra}</div>`;

/** A byte-range reader over the File — nothing here ever holds the whole image. */
function readerFor(file) {
  return async (off, len) => {
    if (off >= file.size) return new Uint8Array(0);
    const end = Math.min(file.size, off + len);
    return new Uint8Array(await file.slice(off, end).arrayBuffer());
  };
}

async function onFile(file) {
  state.file = file;
  state.read = readerFor(file);
  show(`<div class="dr-working">${T('scanning')}</div>`);
  window.__stage && window.__stage('scanning');

  let volumes = [];
  try { volumes = await findVolumes(state.read, file.size); } catch (_) { /* handled below */ }
  state.volumes = volumes;

  const walkable = volumes.filter((v) => v.walkable);
  const named = volumes.filter((v) => !v.walkable && v.kind !== 'unknown');

  let entries = [];
  if (walkable.length) {
    try {
      const v = walkable[0];
      const res = await listTree(state.read, v.bpb);
      state.fat = res.fat;
      state.bpb = v.bpb;
      entries = res.files.map((f) => ({ ...f, plan: planRead(f, v.bpb, res.fat) }));
    } catch (_) { entries = []; }
  }
  state.entries = entries;

  /* What the image turned out to be. This is the single most useful number
   * this tool can report: whether people arrive with FAT (we walk it), with
   * exFAT/NTFS (carving only, names lost) or with something we do not
   * recognise at all — which decides what to build next. */
  window.__stage && window.__stage('analyzed');
  window.__aeFunnel && window.__aeFunnel('analyzed', {
    fs: volumes.map((v) => v.kind).join(',') || 'none',
    entries: entries.length,
    sizeMB: Math.round(file.size / 1048576),
  });
  if (!volumes.length && !entries.length) {
    window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'no_filesystem', sizeMB: Math.round(file.size / 1048576) });

    /* "No file system" has two very different causes and they were answered
     * identically. A card that was formatted really has no file system, and
     * carving is exactly right for it. But a photo, a video or an archive has
     * no file system either — and of the 29 files brought to this tool in a
     * month, 26 had none, sized 0 to 3230 MB. They were not damaged images;
     * they were not images. Carving them finds nothing and the person is told
     * "recovered: 0", which reads as "your data is gone".
     *
     * The same signature table the rest of the site uses answers this in one
     * call: if the bytes at offset 0 name a real format, this is a FILE, and
     * the useful reply is what it is, which tool handles it, and how to make
     * an actual image. */
    try {
      const { diagnose } = await import('../file-doctor/diagnose.js');
      const d = await diagnose(await state.read(0, Math.min(file.size, 1 << 20)), file.name, {});
      if (d && d.format && d.family && d.family !== 'system') {
        window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'not_a_disk_image', fmt: d.format });
        const urls = window.__DR_URLS || {};
        const next = d.repair && urls[d.repair]
          ? `<div class="dr-actions"><a class="dr-btn" href="${urls[d.repair]}">${T('notimg_take_it')}</a></div>` : '';
        show(card('warn', T('verdict_not_an_image'),
          T('body_not_an_image', { label: d.label || d.format }), next)
          + `<section class="dr-block"><h3>${T('notimg_how_title')}</h3>
               <p class="dr-body">${T('notimg_how_body')}</p></section>`);
        return;
      }
    } catch (_) { /* inspection is a courtesy; carving still runs without it */ }
  } else if (!entries.length && volumes.some((v) => !v.walkable)) {
    window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'fs_not_walkable', fs: volumes.map((v) => v.kind).join(',') });
  }

  const live = entries.filter((e) => !e.deleted);
  const del = entries.filter((e) => e.deleted && !e.plan.conflicts.length && e.plan.clusters.length);
  const gone = entries.filter((e) => e.deleted && (e.plan.conflicts.length || !e.plan.clusters.length));

  const rows = [
    [T('stat_size'), human(file.size)],
    [T('stat_volumes'), volumes.length ? volumes.map((v) => T(`fs_${v.kind}`) || v.kind).join(', ') : T('fs_none')],
    entries.length ? [T('stat_live'), String(live.length)] : null,
    del.length ? [T('stat_deleted'), String(del.length)] : null,
    gone.length ? [T('stat_overwritten'), String(gone.length)] : null,
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('');

  const notes = named.length
    ? `<section class="dr-block"><h3>${T('note_title')}</h3><p class="dr-body">${
        T('note_unwalkable', { fs: named.map((v) => T(`fs_${v.kind}`) || v.kind).join(', ') })}</p></section>`
    : '';

  const listing = entries.length
    ? `<section class="dr-block"><h3>${T('found_title', { n: String(entries.length) })}</h3>
         <ul class="dr-list">${entries.slice(0, 400).map((e) => {
           const tag = !e.deleted ? '' : e.plan.conflicts.length
             ? `<span class="dr-bad">${T('tag_overwritten')}</span>`
             : `<span class="dr-warn">${T('tag_deleted')}</span>`;
           return `<li><code>${esc(e.path)}</code> — ${human(e.size)} ${tag}</li>`;
         }).join('')}</ul></section>`
    : '';

  show(card(entries.length || volumes.length ? 'ok' : 'warn',
      entries.length ? T('verdict_fs') : volumes.length ? T('verdict_fs_empty') : T('verdict_no_fs'),
      entries.length
        ? T('body_fs', { live: String(live.length), deleted: String(del.length) })
        : T('body_no_fs'),
      `<div class="dr-actions">
         ${entries.length ? `<button type="button" class="dr-btn" id="dr-restore">${T('restore')}</button>` : ''}
         <button type="button" class="dr-btn ${entries.length ? 'dr-btn-ghost' : ''}" id="dr-carve">${T('carve')}</button>
       </div>`)
    + `<section class="dr-block"><h3>${T('stats_title')}</h3><table class="dr-stats">${rows}</table></section>`
    + notes + listing);

  const r = el('dr-restore'); if (r) r.addEventListener('click', restoreFromFs);
  el('dr-carve').addEventListener('click', runCarve);
}

/** Where the recovered files go. A folder if the browser allows it, else a zip. */
async function pickSink() {
  if (typeof window.showDirectoryPicker === 'function') {
    let dir;
    try { dir = await window.showDirectoryPicker({ mode: 'readwrite', id: 'recover-dest' }); }
    catch (_) { return null; }
    return {
      kind: 'folder',
      async write(path, bytes) {
        const parts = path.split('/');
        let d = dir;
        for (const seg of parts.slice(0, -1)) d = await d.getDirectoryHandle(seg, { create: true });
        const fh = await d.getFileHandle(parts[parts.length - 1], { create: true });
        const w = await fh.createWritable();
        await w.write(bytes);
        await w.close();
        if (window.__deliveredToDisk) window.__deliveredToDisk(path, bytes, 'folder');
      },
      async finish() { return null; },
    };
  }
  /* No folder picker (Firefox, Safari): collect into a zip instead. The zip
   * builder is the archive module's, not a second implementation.
   *
   * A zip is held in memory, so it gets a budget. A card can yield tens of
   * gigabytes, and a tab that dies at 90 % returns nothing at all — better to
   * stop at a size the browser survives and say why. */
  const files = [];
  const BUDGET = 512 << 20;
  let held = 0, dropped = 0;
  return {
    kind: 'zip',
    get dropped() { return dropped; },
    async write(path, bytes) {
      if (held + bytes.length > BUDGET) { dropped++; return; }
      held += bytes.length;
      files.push({ path, bytes });
    },
    async finish() {
      const { buildZip } = await import('../archive/archive-repair-core.js');
      return new Blob([await buildZip(files)], { type: 'application/zip' });
    },
  };
}

function progressPanel(title) {
  show(`<div class="dr-card dr-tone-ok"><p class="dr-title">${title}</p>
      <p class="dr-body" id="dr-now">…</p>
      <div class="dr-bar"><div class="dr-bar-fill" id="dr-bar" style="width:0%"></div></div>
      <div class="dr-actions"><button type="button" class="dr-btn dr-btn-ghost" id="dr-stop">${T('stop')}</button></div>
    </div>`);
  state.abort = new AbortController();
  el('dr-stop').addEventListener('click', () => state.abort.abort());
}

function tick(done, total, label) {
  const now = el('dr-now'), bar = el('dr-bar');
  if (now) now.textContent = label;
  if (bar && total) bar.style.width = Math.round(done / total * 100) + '%';
}

async function restoreFromFs() {
  const sink = await pickSink();
  if (!sink) return;
  const run = window.__toolLimit ? window.__toolLimit.begin('disk-recovery') : null;
  if (window.__toolLimit && !run) return;

  progressPanel(T('verdict_restoring'));
  const good = [], doubtful = [], skipped = [];
  let done = 0;

  for (const e of state.entries) {
    if (state.abort.signal.aborted) break;
    done++;
    tick(done, state.entries.length, T('now', { path: e.path, done: String(done), total: String(state.entries.length) }));
    if (e.plan.conflicts.length || !e.plan.clusters.length) { skipped.push(e); continue; }
    let bytes;
    try { bytes = await readEntry(state.read, state.bpb, e.plan, e.size); } catch (_) { skipped.push(e); continue; }
    if (!bytes.length) { skipped.push(e); continue; }

    /* The same check the carver applies: a deleted file whose clusters were
     * assumed contiguous can come back scrambled, and the person deserves to be
     * told which ones rather than discovering it one photo at a time. */
    const v = await verify(bytes, { format: guessFormat(e.name), probe: guessFormat(e.name), ext: extOf(e.name) });
    const path = v.ok ? e.path : `unverified/${e.path}`;
    try { await sink.write(path, bytes); } catch (_) { skipped.push(e); continue; }
    (v.ok ? good : doubtful).push({ path: e.path, size: bytes.length, issues: v.issues });
  }

  if (good.length || doubtful.length) run && run.commit(); else run && run.abort();
  await finish(sink, good, doubtful, skipped.map((e) => ({ path: e.path, size: e.size })), T('verdict_restored'));
}

async function runCarve() {
  const sink = await pickSink();
  if (!sink) return;
  const run = window.__toolLimit ? window.__toolLimit.begin('disk-recovery') : null;
  if (window.__toolLimit && !run) return;

  progressPanel(T('verdict_carving'));
  const res = await carveAll(state.read, state.file.size, (p, b) => sink.write(p, b), {
    signal: state.abort.signal,
    onProgress: ({ at, size, found }) => tick(at, size, T('now_scan', { pct: String(Math.round(at / size * 100)), n: String(found) })),
    onFile: ({ done, total, name }) => tick(done, total, T('now', { path: name, done: String(done), total: String(total) })),
  });

  if (res.good.length || res.doubtful.length) run && run.commit(); else run && run.abort();
  await finish(sink, res.good.map((r) => ({ path: r.name, size: r.length, issues: r.issues })),
    res.doubtful.map((r) => ({ path: r.name, size: r.length, issues: r.issues })), [], T('verdict_carved'));
}

const extOf = (n) => (String(n).split('.').pop() || '').toLowerCase();
const FORMAT_BY_EXT = {
  jpg: 'jpeg', jpeg: 'jpeg', png: 'png', gif: 'gif', pdf: 'pdf',
  zip: 'zip', docx: 'zip', xlsx: 'zip', pptx: 'zip',
  mp4: 'isobmff', mov: 'isobmff', m4a: 'isobmff', heic: 'isobmff',
  wav: 'riff', avi: 'riff', webp: 'riff', mp3: 'mp3', flac: 'flac', ogg: 'ogg',
  bmp: 'bmp', psd: 'psd', tif: 'tiff', tiff: 'tiff', db: 'sqlite', sqlite: 'sqlite',
};
const guessFormat = (name) => FORMAT_BY_EXT[extOf(name)] || null;

async function finish(sink, good, doubtful, skipped, title) {
  let dl = '';
  let overflow = '';
  if (sink.kind === 'zip') {
    window.__DR_ZIP = await sink.finish();
    dl = `<div class="dr-actions"><button type="button" class="dr-btn" id="dr-save">${T('save_zip')}</button></div>`;
    if (sink.dropped) overflow = `<section class="dr-block"><h3>${T('zip_full_title')}</h3>
      <p class="dr-body">${T('zip_full_desc', { n: String(sink.dropped) })}</p></section>`;
  }

  const rows = [
    [T('stat_ok'), String(good.length)],
    doubtful.length ? [T('stat_doubtful'), String(doubtful.length)] : null,
    skipped.length ? [T('stat_skipped'), String(skipped.length)] : null,
    [T('stat_bytes'), human(good.concat(doubtful).reduce((n, r) => n + r.size, 0))],
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('');

  const list = (items, t, d) => items.length
    ? `<section class="dr-block"><h3>${t}</h3><p class="dr-body">${d}</p>
         <ul class="dr-list">${items.slice(0, 300).map((r) =>
           `<li><code>${esc(r.path)}</code> — ${human(r.size)}${
             r.issues && r.issues.length ? ` <span class="dr-warn">${esc(r.issues.join(', '))}</span>` : ''}</li>`).join('')}</ul></section>`
    : '';

  show(card(doubtful.length || skipped.length ? 'warn' : 'ok', title,
      T('body_finished', { n: String(good.length), doubtful: String(doubtful.length) }), dl)
    + `<section class="dr-block"><h3>${T('stats_title')}</h3><table class="dr-stats">${rows}</table></section>`
    + overflow
    + list(doubtful, T('doubtful_title', { n: String(doubtful.length) }), T('doubtful_desc'))
    + list(skipped, T('skipped_title', { n: String(skipped.length) }), T('skipped_desc')));

  const save = el('dr-save');
  if (save) save.addEventListener('click', () => window.__downloadFile(window.__DR_ZIP, 'recovered.zip'));

  window.__aeToolComplete && window.__aeToolComplete({
    ok: good.length, doubtful: doubtful.length, skipped: skipped.length, sink: sink.kind });
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('drDrop');
  if (!zone) return;
  zone.addEventListener('tool-uploader:file', (ev) => {
    const f = ev.detail && (ev.detail.file || (ev.detail.files && ev.detail.files[0]));
    if (f) onFile(f);
  });
  // The uploader's × removed the file; the report below describes it, so it goes too.
  zone.addEventListener('tool-uploader:clear', () => {
    const out = el('dr-out');
    if (out) { out.innerHTML = ''; out.hidden = true; }
  });
});

export { onFile };
